import { Scenario } from './types';

// Scenario Mode — guided walkthroughs for the SFI annual conference demo.
// Mill IDs cross-checked against src/data/placeholder-mills.json — the S3
// path code is resolved via getMillById at consumer sites.
//
// Editorial conventions for step narratives:
// - First sentence must stand alone (mobile clamps to 3 lines).
// - Define acronyms inline on first use within each scenario (each
//   scenario stands alone — visitors won't have read the others).
// - Speak about Forest Ownership as "ownership" / "private timberlands" /
//   "family + corporate" — never "certified" / "certification".
// - Per-tree biomass: never collapse "Total Tree Biomass" (incl. roots)
//   and "Above-Ground Biomass" (AGB). The bottom-up vs top-down agreement
//   number is AGB-only on both sides.

export const SCENARIOS: Scenario[] = [
  {
    id: 'carbon_offense',
    name: 'Carbon Offense',
    emoji: '🌿',
    sector: 'Compliance Monitoring',
    region: 'Southeast · Alabama Coastal Plain',
    question: 'How well is carbon being maintained on private timberlands within the supply shed?',
    synopsis:
      'Track 25 years of carbon change across this Alabama supply shed, broken out by family, corporate, and other ownership classes.',
    millId: '488fe922-23ac-4057-b2cf-d65572b3c281',
    driveTime: 90,
    steps: [
      {
        title: 'AGB on the ownership mosaic',
        narrative:
          "Aboveground biomass (AGB) — the dry weight of trees standing on the land — painted across this Alabama Coastal Plain supply shed, on top of the full ownership mosaic. The family and corporate classes together make up the private timberland base; federal, state, local, and tribal lands sit alongside.",
        activeLayers: ['aboveground_biomass', 'forest_ownership'],
      },
      {
        title: '25-year carbon trend, in one map',
        narrative:
          "Where biomass gained or lost ground over the last 25 years. Each pixel is the difference between two five-year averages — 2000–04 and 2021–25 — so the map shows the durable trend, not single-year noise. Green pixels gained biomass; red pixels lost it. The bars below show monthly tree-cover-loss alerts (LUCA) — the recent disturbance events whose cumulative outcome is what the map is showing.",
        activeLayers: ['aboveground_biomass', 'forest_ownership'],
        overlayVariants: { aboveground_biomass: 'change' },
        auxiliaryChart: 'luca_monthly',
      },
      {
        title: 'Private timberlands, isolated',
        narrative:
          "Now isolating the private timberland base — family and corporate ownership only — and breaking the carbon-change story out per class. This is the kind of multi-decadal record markets and regulators increasingly want: a verifiable view of where carbon was maintained, gained, or lost on the private lands that feed mill supply chains.",
        activeLayers: ['aboveground_biomass', 'forest_ownership'],
        overlayVariants: {
          aboveground_biomass: 'change',
          forest_ownership: 'private_focus',
        },
        insight: 'private_timberland_carbon_maintenance',
      },
    ],
  },
  {
    id: 'carbon_defense',
    name: 'Carbon Defense',
    emoji: '🔥',
    sector: 'Resilience',
    region: 'Pacific Northwest · Oregon South Coast Range',
    question: 'Where does proactive management create the most value?',
    synopsis:
      'Layer wildfire hazard, stand density, and ownership to find the highest-value thinning candidates in this Oregon supply shed.',
    millId: '8007c629-f2ef-4c95-839a-b83750003cee',
    driveTime: 90,
    steps: [
      {
        title: 'Wildfire hazard across the shed',
        narrative:
          "Wildfire Hazard Potential (WHP) across this Coos Bay supply shed — a USFS measure of how readily fire spreads under typical fire-weather conditions. The shed anchors on the southern Oregon coast and extends inland through the Coast Range. High-WHP zones are the biggest threat to timber supply, watershed function, and standing carbon.",
        activeLayers: ['fire_risk'],
      },
      {
        title: 'Where overstocking meets fire risk',
        narrative:
          "Cross-referencing with Relative Density (RD) — how packed a stand is compared to its biological maximum. Stands that are both overstocked (RD above 0.8) and in high-WHP zones are the prime candidates for thinning to reduce fuel loads and improve resilience.",
        activeLayers: ['fire_risk', 'stand_density'],
      },
      {
        title: 'Whose acres? Public vs. private',
        narrative:
          "Adding the Ownership layer — family, corporate, federal, state, local, tribal — to see which at-risk acres are private timberlands and which are public. Different landowners need different management tools and incentives, so this distinction matters before any intervention plan is drawn up.",
        activeLayers: ['fire_risk', 'stand_density', 'forest_ownership'],
      },
      {
        title: 'Top thinning candidates, ranked',
        narrative:
          "The priority acres themselves — private timberlands painted by a 5-tier score from a WHP × RD decision matrix. Red zones are the top thinning candidates: high or very-high fire hazard combined with dense or overstocked stands. The matrix card on the right shows which WHP × RD cells are driving the spectrum.",
        activeLayers: ['resilience_priority'],
        insight: 'resilience_priority_matrix',
      },
    ],
  },
  {
    id: 'individual_tree',
    name: 'The Individual Tree',
    emoji: '🌲',
    sector: 'Carbon Accounting',
    region: 'West · California Sierra Nevada',
    question: 'What does forest carbon accounting look like at the tree?',
    synopsis:
      'Drop from 100m satellite biomass to 5m canopy structure to individual tree crowns in the Sentinel Creek headwaters.',
    millId: 'e952bec6-0f78-438a-898b-c3680d625bd4',
    driveTime: 60,
    steps: [
      {
        title: '100m AGB · the satellite view',
        narrative:
          "El Portal sits at the gateway to Yosemite, between Stanislaus and Sierra National Forests — one of the densest forest-carbon stores in the U.S. The 100m aboveground biomass (AGB) layer is the satellite-eye view: standing biomass in megagrams per hectare across each 100×100m cell. It tells you what's there — but not what's inside each cell.",
        activeLayers: ['aboveground_biomass'],
        // Wide shot: mill anchored, whole 60-min shed in frame. zoom 10 is
        // the AGB tile pyramid's top (z=5..10) — going tighter would render
        // blank tiles, but staying at 9 left too much slack on mobile where
        // the panel doesn't eat half the viewport. Center is offset ~0.08°
        // SOUTH of the shed centroid so the L-shaped shed shifts up in
        // viewport — its south arm clears the year slider, and its north
        // arm sits below the scenario card overlay. Desktop is a small
        // upward shift, still well within the right-of-panel map area.
        mapFocus: { center: [37.604, -119.6715], zoom: 10 },
      },
      {
        title: '5m canopy · structure resolved',
        narrative:
          "5m canopy height — the height of vegetation in each 5×5m cell — resolves the structure that 100m AGB averaged out. Mature crowns above mixed-conifer mid-stories, recent harvests, riparian corridors all become visible. The carbon claim is still aggregated across pixels, though; the individual tree is still implicit.",
        // AGB drops out here: its pyramid only goes to z=10, so keeping it
        // active at the Step 2 zoom would render blank tiles and undercut
        // the resolution-shift punchline. The narrative still references
        // the 100m view ("the heterogeneity the 100m layer averaged out")
        // because the user just saw it in Step 1.
        activeLayers: ['canopy_height'],
        // Tighten onto the mixed-conifer terrain just NE of El Portal —
        // close enough to the Sentinel Creek headwaters that Step 3's CTA
        // fly is mostly a zoom-in beat, not a long lateral pan. zoom 13
        // is where the 5 m canopy texture is unambiguous on screen.
        mapFocus: { center: [37.715, -119.605], zoom: 13 },
      },
      {
        title: 'Every tree, attributed',
        narrative:
          "Now the resolution shifts to the individual tree. Every tree in the shed is attributed with biomass, height, diameter, and forest type. Summing the trees gives a bottom-up estimate that can be cross-checked against the 100m AGB layer — two independent methods of measuring the same forest. The map is dropping into the Sentinel Creek headwaters now to show the crowns themselves.",
        // Keep canopy_height painted as visual context while the user
        // reads the insight + decides to click the CTA. Dropping it would
        // leave the map empty (the per-crown overlay is button-gated).
        activeLayers: ['canopy_height', 'tree_crown_segmentation'],
        // Same camera as Step 2 — no fly between Step 2 and Step 3. The
        // CTA's two-stage cinematic fly handles the zoom-in to native
        // resolution from this vantage.
        mapFocus: { center: [37.715, -119.605], zoom: 13 },
        insight: 'per_tree_carbon_rollup',
        // Auto-trigger on both mobile and desktop: advancing into this
        // step from Step 2 fires the cinematic fly into Sentinel Creek
        // directly, so users don't have to hunt for the "View individual
        // trees" button to see the punchline. The CTA tile in
        // ScenarioPanel still renders so users can exit native view
        // from the panel.
        autoActivateNativeView: true,
      },
    ],
  },
];

export const findScenarioById = (id: string | null | undefined): Scenario | null => {
  if (!id) return null;
  return SCENARIOS.find((s) => s.id === id) ?? null;
};
